import React, { useMemo, useState } from "react";
import {
  DATASET_COLLECTION_KIND_LABEL,
  DATASET_FORMAT_LABEL,
  DatasetCatalogItem,
  datasetCatalogApi,
  datasetDisplayTitle,
  datasetProvenanceLabel,
  isDatasetInstalledFromCatalog,
  isDatasetPublishedToCatalog,
  isLayerGroupId,
} from "../../../services/datasetCatalog";
import { DatasetDataflowUsageSection, useDatasetDataflowUsage } from "./DatasetDataflowUsage";
import {
  CollectionInfoSection,
  CollectionStrip,
  useCollectionStatus,
} from "./DatasetCollectionPanel";
import { DetailLink } from "./DetailLink";
import { useToastContext } from "../../../providers/ToastProvider";
import {
  downstreamFromDataflowUsage,
  formatNodeTypeLabel,
  lineageUsageSummary,
  upstreamOriginCaption,
  useDatasetLineage,
  type DatasetLineage,
  type DatasetLineageNodeUsageRef,
  type LineageStatus,
} from "../../../services/datasetLineage";
import { CatalogDetailHeader } from "../../catalog/CatalogDetailHeader";
import { CopyButton } from "../../CopyButton";
import { datasetReference } from "../../../services/datasetCatalog";
import {
  absoluteDate,
  datasetCount,
  formatBytes,
  formatClass,
  relativeTime,
} from "./datasetDetailHelpers";
import styles from "./DatasetDetailPanel.module.css";
import { DatasetSchemaPanel } from "./DatasetSchemaPanel";
import { DatasetTablePreview } from "./DatasetTablePreview";
import { useDatasetResolvedSchema } from "./useDatasetResolvedSchema";

const TABS = ["Overview", "Schema", "Table Preview", "Lineage"] as const;

const LINEAGE_STATUS_LABEL: Record<LineageStatus, string> = {
  active: "Active",
  stale: "Stale",
  missing: "Missing",
  unresolved: "Unresolved",
};

function statusPillClass(status: LineageStatus): string {
  return status === "stale"
    ? `${styles.statusPill} ${styles.statusStale}`
    : `${styles.statusPill} ${styles.statusBroken}`;
}

/**
 * Renders only a status that needs attention. "Active" is the expected state of
 * every row, so pinning a pill on it was colour carrying no signal - the absence
 * of a pill now means healthy, and the eye goes straight to the rows that aren't.
 */
const LineageStatusPill: React.FC<{ status: LineageStatus }> = ({ status }) => {
  if (status === "active") return null;
  return <span className={statusPillClass(status)}>{LINEAGE_STATUS_LABEL[status]}</span>;
};

function usageDisplayName(usage: DatasetLineageNodeUsageRef): string {
  if (usage.nodeName) return usage.nodeName;
  return `Node ${usage.nodeId.slice(0, 8)}`;
}

/**
 * The node type badge, rendered only when it says something the name above it
 * doesn't. A node keeps its type as its name unless it was renamed, so on most
 * rows the badge was the same words a second time in uppercase.
 */
const NodeTypeBadge: React.FC<{
  nodeType: string | undefined;
  displayName: string;
}> = ({ nodeType, displayName }) => {
  if (!nodeType) return null;
  const label = formatNodeTypeLabel(nodeType);
  if (label.toLowerCase() === displayName.trim().toLowerCase()) return null;
  return <span className={styles.lineageNodeBadge}>{label}</span>;
};

const DownstreamUsageCards: React.FC<{
  usages: DatasetLineageNodeUsageRef[];
}> = ({ usages }) => (
  <>
    {usages.map((usage) => (
      <article className={styles.consumedCard} key={usage.nodeId}>
        <div>
          <strong>{usageDisplayName(usage)}</strong>
          <NodeTypeBadge
            nodeType={usage.nodeType}
            displayName={usageDisplayName(usage)}
          />
          <span>
            {usage.status === "unresolved"
              ? "Reference not found in this dataflow"
              : usage.dataflowName
                ? `Dataflow: ${usage.dataflowName}`
                : "Current dataflow"}
          </span>
        </div>
        <LineageStatusPill status={usage.status} />
      </article>
    ))}
  </>
);

const UpstreamCards: React.FC<{
  dataset: DatasetCatalogItem;
  lineage: DatasetLineage;
}> = ({ dataset, lineage }) => {
  const generatingNode = lineage.upstream.generatingNode;
  // What fed the producing node, from the dataset's persisted upstreamInputs.
  // Only computed datasets carry any, so this whole block is absent for
  // anything imported.
  const inputNodes = lineage.upstream.inputNodes ?? [];
  const sourceDatasets = lineage.upstream.sourceDatasets ?? [];
  const inputCount = inputNodes.length + sourceDatasets.length;
  return (
    <>
      <article className={styles.originCard}>
        <div>
          {generatingNode ? (
            <>
              <strong>
                Generated by{" "}
                {generatingNode.nodeName || `node ${generatingNode.nodeId.slice(0, 8)}`}
              </strong>
              <NodeTypeBadge
                nodeType={generatingNode.nodeType}
                displayName={generatingNode.nodeName || ""}
              />
              <span>
                {upstreamOriginCaption(dataset)}
                {generatingNode.dataflowName
                  ? ` · in ${generatingNode.dataflowName}`
                  : ""}
              </span>
            </>
          ) : (
            <>
              <strong>{lineage.upstream.originLabel}</strong>
              <span>{DATASET_FORMAT_LABEL[dataset.format]} dataset</span>
            </>
          )}
        </div>
      </article>
      {inputCount > 0 ? (
        <>
          <p className={styles.lineageLabel}>Inputs ({inputCount})</p>
          {inputNodes.map((input) => (
            <article className={styles.originCard} key={`node:${input.nodeId}`}>
              <div>
                <strong>{input.nodeName || `node ${input.nodeId.slice(0, 8)}`}</strong>
                <NodeTypeBadge
                  nodeType={input.nodeType}
                  displayName={input.nodeName || ""}
                />
                <span>Node input</span>
              </div>
            </article>
          ))}
          {sourceDatasets.map((source) => (
            <article className={styles.originCard} key={`dataset:${source.datasetId}`}>
              <div>
                {/* The id in title=: a computed id is a uuid pair, so the label
                    is derived and the full value stays available on hover. */}
                <strong data-tooltip={source.datasetId}>
                  {source.title || source.datasetId}
                </strong>
                <span>Dataset input</span>
              </div>
            </article>
          ))}
        </>
      ) : null}
      {isDatasetInstalledFromCatalog(dataset) ? (
        <div className={styles.hubSourceCard}>
          <strong>Added from Data Catalog</strong>
          <span>
            {dataset.sourceLabel || datasetProvenanceLabel(dataset.origin, dataset.format)} · v1
          </span>
        </div>
      ) : null}
    </>
  );
};

const LineageStates: React.FC<{
  lineage: DatasetLineage;
  canvasAvailable: boolean;
}> = ({ lineage, canvasAvailable }) => {
  if (lineage.status.hasUnresolvedReferences) {
    return (
      <p className={styles.lineagePartialNote}>
        Lineage is partially available. Some node or dataflow references could
        not be resolved.
      </p>
    );
  }
  // On canvas-less surfaces we show usage resolved from saved dataflow specs.
  // Only prompt to open a dataflow when there's nothing resolved to show —
  // otherwise the note contradicts the usage listed right below it.
  if (!canvasAvailable && lineage.downstream.consumingNodes.length === 0) {
    return (
      <p className={styles.lineagePartialNote}>
        Open this dataset from a dataflow to see live usage.
      </p>
    );
  }
  return null;
};

/** Expanded lineage view rendered in the center column for the Lineage tab. */
const LineageMainSection: React.FC<{
  dataset: DatasetCatalogItem;
  lineage: DatasetLineage;
  canvasAvailable: boolean;
  onFollowLink?: (to: string) => void;
}> = ({ dataset, lineage, canvasAvailable, onFollowLink }) => {
  const { consumingNodes } = lineage.downstream;
  return (
    <div className={styles.lineageSection}>
      <div className={styles.lineageHeader}>
        <h2>
          Dataflows <span className={styles.liveBadge}>live</span>
        </h2>
        <p>Dataflows that generate or consume this dataset</p>
      </div>

      <LineageStates lineage={lineage} canvasAvailable={canvasAvailable} />

      <p className={styles.lineageLabel}>Generated by</p>
      <UpstreamCards dataset={dataset} lineage={lineage} />

      <p className={styles.lineageLabel}>Consumed by ({consumingNodes.length})</p>
      {consumingNodes.length > 0 ? (
        <DownstreamUsageCards usages={consumingNodes} />
      ) : (
        <p className={styles.lineageEmpty}>
          No nodes or dataflows are currently using this dataset.
        </p>
      )}

      {/* Cross-dataflow usage (resolved from saved specs by the backend, so it
          works even without a live canvas). */}
      <DatasetDataflowUsageSection datasetId={dataset.id} onFollowLink={onFollowLink} />
    </div>
  );
};


/** A parameter's id as a label: "area" is "Area", "maxImages" is "Max images". */
export function parameterLabel(id: string): string {
  const words = id.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** What a download was narrowed by, as text: a place or a box, dates, values. */
export function parameterText(value: unknown): string {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const v = value as Record<string, unknown>;
    if (Array.isArray(v.box)) {
      const box = (v.box as number[]).map((n) => n.toFixed(4)).join(", ");
      return v.label ? `${String(v.label)} (${box})` : box;
    }
    if (v.names && typeof v.names === "object") {
      const names = v.names as { geocodeArea?: string; areas?: string[] };
      return `${(names.areas ?? []).join(", ")} in ${names.geocodeArea ?? ""}`;
    }
    if ("start" in v || "end" in v) return `${String(v.start ?? "")} to ${String(v.end ?? "")}`.trim();
    return JSON.stringify(v);
  }
  if (Array.isArray(value)) return value.map(String).join(", ");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

export interface DatasetDetailPanelProps {
  dataset: DatasetCatalogItem | null;
  loading?: boolean;
  error?: string | null;
  /**
   * True only where a live ReactFlow canvas is mounted behind this panel —
   * i.e. the in-canvas Data Catalog drawer. Drives whether lineage is read
   * from the canvas or from the backend's saved specs. The browse pages open
   * this panel too, and have no canvas.
   */
  canvasAvailable?: boolean;
  dataflowId?: string | null;
  liveOutputs?: Array<{ node_id: string; filename: string; data_type?: string }>;
  initialTab?: (typeof TABS)[number];
  /** Called after publish/unpublish so the parent can refetch the dataset. */
  onMutated?: () => void;
  /** How an in-app link in these details is followed; see `DetailLink`. */
  onFollowLink?: (to: string) => void;
  /** In the account's "all projects" list. Left out until it is known, so the
   *  row never guesses. */
  inAllProjects?: boolean;
}

export const DatasetDetailPanel: React.FC<DatasetDetailPanelProps> = ({
  dataset,
  loading = false,
  error = null,
  canvasAvailable = false,
  dataflowId = null,
  liveOutputs,
  initialTab = "Overview",
  onMutated,
  onFollowLink,
  inAllProjects,
}) => {
  const { showToast } = useToastContext();
  const [activeTab, setActiveTab] = useState<(typeof TABS)[number]>(initialTab);
  const [exporting, setExporting] = useState(false);
  // The standalone catalog page renders without a canvas, so live usage
  // cannot be resolved there and lineage is flagged as partial.
  const lineage = useDatasetLineage(dataset, { dataflowId, canvasAvailable });
  // Backend cross-dataflow usage (resolved from saved specs) — used to populate
  // the downstream-nodes list on canvas-less surfaces (the standalone catalog/
  // browse page) where live lineage resolves no consumer nodes.
  const dataflowUsage = useDatasetDataflowUsage(dataset?.id);
  const backendDownstream = useMemo(
    () => downstreamFromDataflowUsage(dataflowUsage),
    [dataflowUsage],
  );
  // Shared schema resolution so the header/info counts match the schema panel.
  const resolvedSchema = useDatasetResolvedSchema(dataset, dataflowId, liveOutputs);
  const fields = resolvedSchema.fields;
  // What to paste into a node, and where the bytes are. One helper decides, so
  // no surface has to reason about ids versus paths (#206).
  const reference = useMemo(
    () => (dataset ? datasetReference(dataset) : null),
    [dataset],
  );
  const collection = useCollectionStatus(
    dataset?.format === "collection" ? dataset.id : undefined,
  );

  const columnsLabel =
    fields.length > 0
      ? fields.length.toLocaleString()
      : resolvedSchema.fetching
        ? "…"
        : "Unknown";
  const rootClass = styles.modalRoot;

  if (loading && !dataset) {
    return <p className={styles.loading}>Loading dataset...</p>;
  }

  if (error) {
    return <p className={styles.error}>{error}</p>;
  }

  if (!dataset || !lineage) {
    return <p className={styles.loading}>Dataset not found.</p>;
  }

  const countLabel = datasetCount(dataset);
  const tags = dataset.tags.length > 0 ? dataset.tags : [dataset.format];
  // Prefer live-canvas consumers; fall back to backend usage when the canvas
  // resolved none (e.g. the standalone catalog page has no open dataflow).
  const liveConsumingNodes = lineage.downstream.consumingNodes;
  const usingBackendUsage = liveConsumingNodes.length === 0;
  const effectiveLineage: DatasetLineage = usingBackendUsage
    ? { ...lineage, downstream: backendDownstream }
    : lineage;
  const { consumingNodes } = effectiveLineage.downstream;
  const published = isDatasetPublishedToCatalog(dataset);
  // Neither a multi-part bundle, a layer group (whatever format it shows) nor a
  // collection (an index of files kept where they are) is a single exportable file.
  const canExport =
    dataset.format !== "bundle" &&
    dataset.format !== "osm" &&
    dataset.format !== "collection" &&
    !isLayerGroupId(dataset.id);
  const discovered = dataset.discoverySource;
  // A storage source's resource is a folder or a bucket, not a portal page.
  // Which words describe where the bytes came from: a file the person
  // downloaded by hand, then a storage source's files, then a portal download.
  const manual = Boolean(discovered?.manual);
  const fromStorage = Boolean(discovered && !manual && (discovered.fingerprint || discovered.fileCount != null));
  const activeDataset = dataset;

  const handleExport = async () => {
    if (exporting || !canExport) return;
    setExporting(true);
    try {
      await datasetCatalogApi.downloadDataset(activeDataset.id, {
        dataflowId,
        liveOutputs,
        format: activeDataset.format,
      });
    } catch (err) {
      showToast((err as Error)?.message || "Could not export dataset.", "error");
    } finally {
      setExporting(false);
    }
  };

  const showSchemaColumn = activeTab === "Overview";
  // The Lineage tab already renders every card in the center column, so the
  // sidebar keeps only its headline summary: the groups collapse and the
  // cross-dataflow list drops out rather than printing the same rows twice.
  const lineageTabActive = activeTab === "Lineage";
  const bodyClass = showSchemaColumn
    ? styles.inspectorBody
    : `${styles.inspectorBody} ${styles.inspectorBodyTwoCol}`;

  return (
    <main className={`${styles.inspector} ${rootClass}`}>
      <div className={styles.pageHeader}>
        {/* No breadcrumb. It read "DATA CATALOG / <dataset name>" directly above
            a header that already shows the dataset's name in 22px bold, inside
            a panel opened from the Data Catalog - three tellings of one fact,
            and the trail led nowhere because none of it was a link. The peers
            (Agent, Node) never had one. */}

        {/* The shared header, the same one the Agent and Node details views
            render. There used to be two stylesheets and three results - two
            title sizes, three paddings, and the action in a different place in
            each - so lining them up meant editing whichever file you were in
            and they came apart again straight away.

            No publish control in it: this panel is the DETAILS view, and it
            carried its own red Unpublish with no ownership gate at all, so a
            dataset the user never published and cannot withdraw offered to
            withdraw it. That decision is made in one place, the Data Catalog
            page's right-hand drawer, which gates on `isUserOwnedDataset`. */}
        <CatalogDetailHeader
          kind="dataset"
          title={datasetDisplayTitle(dataset)}
          actions={
            <button
              className={styles.exportButton}
              type="button"
              onClick={handleExport}
              disabled={exporting || !canExport}
              data-tooltip={
                canExport
                  ? undefined
                  : "This dataset has no single file to export."
              }
            >
              {exporting ? "Exporting…" : "Export"}
            </button>
          }
        >
          <div className={styles.inspectorMeta}>
            <span className={styles.installedBadge}>
              {dataset.installed
                ? "In project"
                : datasetProvenanceLabel(dataset.origin, dataset.format)}
            </span>
            <span className={formatClass(dataset.format, styles)}>
              {DATASET_FORMAT_LABEL[dataset.format]}
            </span>
            {dataset.collection ? (
              <span className={styles.installedBadge}>
                {DATASET_COLLECTION_KIND_LABEL[dataset.collection.kind] ?? dataset.collection.kind}
              </span>
            ) : null}
            {countLabel ? <span>{countLabel}</span> : null}
            {fields.length > 0 ? (
              <>
                <span className={styles.metaDot} aria-hidden="true">·</span>
                <span>{fields.length} columns</span>
              </>
            ) : null}
            {formatBytes(dataset.sizeBytes) ? (
              <>
                <span className={styles.metaDot} aria-hidden="true">·</span>
                <span>{formatBytes(dataset.sizeBytes)}</span>
              </>
            ) : null}
            <span className={styles.metaDot} aria-hidden="true">·</span>
            <span>Updated {relativeTime(dataset.updatedAt)}</span>
          </div>
        </CatalogDetailHeader>

        <nav className={styles.inspectorTabs} aria-label="Dataset detail sections">
          {TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              className={tab === activeTab ? styles.inspectorTabActive : ""}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      <div className={bodyClass}>
        {showSchemaColumn ? (
          <section className={styles.schemaColumn} aria-label="Schema">
            <DatasetSchemaPanel schema={resolvedSchema} />
          </section>
        ) : null}

        <section className={styles.centerColumn} aria-label="Dataset content">
          {activeTab === "Schema" ? (
            <DatasetSchemaPanel schema={resolvedSchema} />
          ) : activeTab === "Lineage" ? (
            <LineageMainSection
              dataset={dataset}
              lineage={effectiveLineage}
              canvasAvailable={canvasAvailable}
              onFollowLink={onFollowLink}
            />
          ) : (
            <div className={styles.previewSection}>
              {dataset.collection ? <CollectionStrip status={collection.status} /> : null}
              <div className={styles.previewSubtab}>
                <span className={styles.previewSubtabActive}>Table Preview</span>
              </div>
              <DatasetTablePreview dataset={dataset} dataflowId={dataflowId} liveOutputs={liveOutputs} />
            </div>
          )}
        </section>

        <aside className={styles.infoColumn} aria-label="Dataset info">
          <div className={styles.infoHeader}>
            <h2>Dataset Info</h2>
          </div>

          {/* How to USE this dataset, above what it contains: the details view
              is where someone comes to find that out, and until now it was the
              one place that did not say (#206). The reference is what goes into
              a node -- the location is shown beside it as information, because
              an absolute path is specific to this machine and pasting it
              produces code that breaks for the next person. */}
          <div className={styles.infoSection}>
            <p className={styles.infoSectionLabel}>Use in a node</p>
            <div className={styles.referenceRow}>
              <code className={styles.referenceCode}>{reference?.code}</code>
              <CopyButton
                value={reference?.code ?? ""}
                label="Copy dataset reference"
                variant="labelled"
              />
            </div>
            {reference?.location ? (
              <p className={styles.referenceLocation} data-tooltip={reference.location}>
                {reference.location}
              </p>
            ) : null}
          </div>

          <div className={styles.infoSection}>
            <p className={styles.infoSectionLabel}>General</p>
            <dl className={styles.infoRows}>
              <div><dt>Format</dt><dd>{DATASET_FORMAT_LABEL[dataset.format]}</dd></div>
              <div><dt>Total {dataset.featureCount != null ? "features" : "records"}</dt><dd>{(dataset.featureCount ?? dataset.rowCount)?.toLocaleString() || "Unknown"}</dd></div>
              <div><dt>Columns</dt><dd>{columnsLabel}</dd></div>
              <div><dt>File size</dt><dd>{formatBytes(dataset.sizeBytes) || "Unknown"}</dd></div>
              {dataset.schema?.crs ? (
                <div><dt>CRS</dt><dd>{dataset.schema.crs}</dd></div>
              ) : null}
              <div><dt>Availability</dt><dd><span className={styles.installedBadge}>{dataset.installed ? "In project" : "Available"}</span></dd></div>
              {/* The two account-level facts the Node and Agent details show,
                  under the same names. */}
              {inAllProjects !== undefined ? (
                <div><dt>In all projects</dt><dd>{inAllProjects ? "Yes" : "No"}</dd></div>
              ) : null}
              <div><dt>In the catalog</dt><dd>{published ? "Published" : "Not published"}</dd></div>
              <div><dt>Imported</dt><dd>{absoluteDate(dataset.createdAt ?? dataset.updatedAt)}</dd></div>
              {/*<div><dt>Last updated</dt><dd title={absoluteDate(dataset.updatedAt)}>{relativeTime(dataset.updatedAt)}</dd></div>*/}
              {dataset.sourceUpdatedAt ? (
                <div><dt>Source updated</dt><dd data-tooltip={absoluteDate(dataset.sourceUpdatedAt)}>{relativeTime(dataset.sourceUpdatedAt)}</dd></div>
              ) : null}
            </dl>
          </div>

          {dataset.collection ? (
            <CollectionInfoSection
              dataset={dataset}
              status={collection.status}
              error={collection.error}
              job={collection.job}
              onCacheFiles={collection.cacheFiles}
              onFollowLink={onFollowLink}
            />
          ) : discovered ? (
            // Where the bytes came from. Without it a downloaded dataset is
            // indistinguishable from a hand-uploaded one, and the question it
            // answers - "which portal is this, and can I go back to it?" - has
            // no other home on this page. A collection says it in its own
            // section, above.
            <div className={styles.infoSection}>
              <p className={styles.infoSectionLabel}>
                {manual ? "Downloaded by hand from" : fromStorage ? "Added from" : "Downloaded from"}
              </p>
              <dl className={styles.infoRows}>
                {discovered.sourceId ? (
                  <div>
                    <dt>{fromStorage ? "Source" : "Portal"}</dt>
                    <dd>
                      <DetailLink
                        to={`/catalog/discovery/${encodeURIComponent(discovered.sourceId)}`}
                        onFollow={onFollowLink}
                      >
                        {discovered.sourceName || discovered.sourceId}
                      </DetailLink>
                    </dd>
                  </div>
                ) : null}
                {discovered.resourceUrl ? (
                  <div>
                    <dt>{discovered.resourceId ? "Resource" : "Link"}</dt>
                    <dd>
                      <a href={discovered.resourceUrl} target="_blank" rel="noreferrer noopener">
                        {discovered.resourceId || discovered.resourceUrl} ↗
                      </a>
                    </dd>
                  </div>
                ) : discovered.resourceId ? (
                  <div>
                    <dt>Resource</dt>
                    <dd>{discovered.resourceId}</dd>
                  </div>
                ) : null}
                {discovered.fileCount != null && discovered.fileCount > 1 ? (
                  <div>
                    <dt>Combined from</dt>
                    <dd>{discovered.fileCount.toLocaleString()} files</dd>
                  </div>
                ) : discovered.sourcePath ? (
                  <div>
                    <dt>File</dt>
                    <dd>{discovered.sourcePath}</dd>
                  </div>
                ) : null}
                {Object.entries(discovered.parameters ?? {}).map(([id, value]) => (
                  <div key={id}>
                    <dt>{parameterLabel(id)}</dt>
                    <dd>{parameterText(value)}</dd>
                  </div>
                ))}
                {discovered.fetchedAt ? (
                  <div>
                    <dt>{manual ? "Imported" : fromStorage ? "Added" : "Downloaded"}</dt>
                    <dd data-tooltip={absoluteDate(discovered.fetchedAt)}>{relativeTime(discovered.fetchedAt)}</dd>
                  </div>
                ) : null}
              </dl>
            </div>
          ) : null}

          <div className={styles.infoSection}>
            <p className={styles.infoSectionLabel}>
              Lineage <span className={styles.liveBadge}>live</span>
            </p>
            <p className={styles.lineageSummary}>
              {lineageUsageSummary(effectiveLineage.downstream)}
            </p>

            <LineageStates lineage={effectiveLineage} canvasAvailable={canvasAvailable} />

            <details className={styles.lineageGroup} open={!lineageTabActive}>
              <summary className={styles.lineageGroupSummary}>
                Downstream usage ({consumingNodes.length})
              </summary>
              {consumingNodes.length > 0 ? (
                <DownstreamUsageCards usages={consumingNodes} />
              ) : (
                <p className={styles.lineageEmpty}>
                  No nodes or dataflows are currently using this dataset.
                </p>
              )}
            </details>

            <details className={styles.lineageGroup} open={!lineageTabActive}>
              <summary className={styles.lineageGroupSummary}>Upstream</summary>
              <UpstreamCards dataset={dataset} lineage={effectiveLineage} />
            </details>

            {/* Cross-dataflow usage — resolved from saved specs by the backend,
                so it works on the canvas-less standalone catalog page too. */}
            {lineageTabActive ? null : (
              <DatasetDataflowUsageSection datasetId={dataset.id} onFollowLink={onFollowLink} />
            )}
          </div>

          <div className={styles.infoSection}>
            <p className={styles.infoSectionLabel}>Tags</p>
            <div className={styles.tagList}>
              {tags.map((tag) => (
                <span className={styles.tagChip} key={tag}>{tag}</span>
              ))}
            </div>
          </div>
        </aside>
      </div>

    </main>
  );
};
